import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiAlertCircle, FiArrowLeft, FiArrowRight, FiCheck, FiX } from "react-icons/fi";
import { useSteffiCopy } from "../content";
import { usePublicLang } from "../../../config/i18n/public";
import { trackAnalyticsEvent } from "../../../utils/analytics";
import {
  NEWSLETTER_AGE_GROUPS,
  NEWSLETTER_AUDIENCES,
  NEWSLETTER_BENEFITS,
} from "../../../config/newsletterSurvey";

const API = process.env.REACT_APP_API_URL;
const WaitlistContext = createContext(() => {});
const SURVEY_TEXT = {
  de: {
    intro: "Drei kurze Fragen helfen uns dabei, SteffiCare noch besser auf eure Bedürfnisse auszurichten.",
    confirmed: "Deine E-Mail-Adresse ist bereits bestätigt.",
    confirmation: "Fast geschafft: Bitte bestätige deine E-Mail-Adresse über den Link, den wir dir geschickt haben.",
    waitlistSaved: "Du stehst auf der Warteliste. Eine Bestätigungs-E-Mail ist nicht notwendig.",
    waitlistBody: "Wir kontaktieren dich, wenn wir dich für den Test auswählen und dir ein Gerät zur Verfügung stellen können. Mit dem Eintrag besteht kein Anspruch auf ein Testgerät.",
    city: "Testort (Stadt)",
    cityOptional: "Stadt (optional)",
    citySearching: "Orte werden gesucht …",
    citySelect: "Bitte wähle einen Ort aus der Vorschlagsliste.",
    cityNoResults: "Kein passender Ort gefunden.",
    cityError: "Die Ortsliste konnte nicht geladen werden.",
    cityAttribution: "© OpenStreetMap",
    phone: "Mobilnummer (optional)",
    audience: "Für wen möchtest du SteffiCare nutzen?",
    ageSelf: "Wie alt bist du?",
    ageOther: "Wie alt ist die Person, für die du SteffiCare nutzen möchtest?",
    ageProfessional: "Für welche Altersgruppe soll SteffiCare hauptsächlich eingesetzt werden?",
    benefit: "Was wäre für dich der größte Mehrwert von SteffiCare?",
    benefitHint: "Wähle bis zu drei Antworten. Die Reihenfolge bestimmt die Priorität.",
    save: "Antworten speichern",
    saving: "Wird gespeichert …",
    thanks: "Vielen Dank für deine Antworten!",
    error: "Die Antworten konnten nicht gespeichert werden.",
    step: "Frage {current} von {total}",
    back: "Zurück",
    next: "Weiter",
  },
  en: {
    intro: "Three short questions help us tailor SteffiCare even better to your needs.",
    confirmed: "Your email address is already confirmed.",
    confirmation: "Almost done: Please confirm your email address using the link we sent you.",
    waitlistSaved: "You are on the waitlist. No confirmation email is required.",
    waitlistBody: "We will contact you if we select you for the test and can provide you with a device. Joining does not guarantee participation or a device.",
    city: "Device test city",
    cityOptional: "City (optional)",
    citySearching: "Searching for cities …",
    citySelect: "Please select a city from the suggestions.",
    cityNoResults: "No matching city found.",
    cityError: "The city list could not be loaded.",
    cityAttribution: "© OpenStreetMap",
    phone: "Mobile number (optional)",
    audience: "Who would you like to use SteffiCare for?",
    ageSelf: "How old are you?",
    ageOther: "How old is the person you would like to use SteffiCare for?",
    ageProfessional: "Which age group would SteffiCare mainly be used for?",
    benefit: "What would be the greatest benefit of SteffiCare for you?",
    benefitHint: "Choose up to three answers. Your selection order sets the priority.",
    save: "Save answers",
    saving: "Saving …",
    thanks: "Thank you for your answers!",
    error: "Your answers could not be saved.",
    step: "Question {current} of {total}",
    back: "Back",
    next: "Next",
  },
};

export const useWaitlist = () => useContext(WaitlistContext);

/** Tailwind's `sm` breakpoint. Below it the three questions do not fit on one
 *  phone screen, and a save button below the fold reads as "nothing left to
 *  do" — so the survey becomes one question per step down there. */
function useIsMobile() {
  const query = "(max-width: 639px)";
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (event) => setMobile(event.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

function OptionButton({ label, selected, badge, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left text-[0.9375rem] transition ${
        selected
          ? "border-[color:var(--accent)] bg-[color:var(--bg-2)] text-[color:var(--ink)]"
          : "border-[color:var(--border)] text-[color:var(--ink-2)] hover:border-[color:var(--accent)]"
      }`}
    >
      {badge != null && (
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[color:var(--accent)] text-xs font-bold text-white">
          {badge}
        </span>
      )}
      {label}
    </button>
  );
}

export function WaitlistProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const open = location.pathname === "/newsletter" || location.pathname === "/waitlist";
  const mode = location.pathname === "/waitlist" ? "waitlist" : "newsletter";
  // Buttons pass their click event; only a string is a prefilled address.
  const openWaitlist = useCallback(
    (email) =>
      navigate(
        { pathname: "/newsletter", search: location.search },
        typeof email === "string" && email ? { state: { email } } : undefined,
      ),
    [location.search, navigate],
  );
  const close = useCallback(
    () => navigate({ pathname: "/angehoerige", search: location.search }, { replace: true }),
    [location.search, navigate],
  );

  return (
    <WaitlistContext.Provider value={openWaitlist}>
      {children}
      <WaitlistModal open={open} mode={mode} onClose={close} initialEmail={location.state?.email} />
    </WaitlistContext.Provider>
  );
}

/** One question per screen. Tapping an option only marks it; moving on is a
 *  separate, deliberate tap — so a mis-tap costs a correction, not a step. */
function SurveyWizard({ steps, survey, lang, surveyText, surveyStatus, onPick, onSave }) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState("next");

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const answered = current.multi ? survey.priorities.length > 0 : Boolean(survey[current.field]);

  function go(target, nextDirection) {
    setDirection(nextDirection);
    setStep(target);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 pb-5 pr-12">
        <button
          type="button"
          onClick={() => go(step - 1, "prev")}
          disabled={step === 0}
          aria-label={surveyText.back}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[color:var(--ink-3)] transition hover:bg-[color:var(--bg-2)] hover:text-[color:var(--ink)] disabled:pointer-events-none disabled:opacity-0"
        >
          <FiArrowLeft className="h-4 w-4" aria-hidden />
        </button>
        <div className="h-1 flex-1 overflow-hidden rounded-full bg-[color:var(--bg-2)]">
          <div
            className="steffi-wizard-bar h-full rounded-full bg-[color:var(--accent)]"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>
        <span className="shrink-0 text-[0.8125rem] font-semibold tabular-nums text-[color:var(--ink-3)]">
          {step + 1}/{steps.length}
        </span>
      </div>

      <div key={step} data-direction={direction} className="steffi-wizard-step min-h-0 flex-1 overflow-y-auto">
        <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.08em] text-[color:var(--ink-3)]">
          {surveyText.step.replace("{current}", step + 1).replace("{total}", steps.length)}
        </p>
        <h3 id="steffi-wizard-title" className="mt-2 text-[1.25rem] font-semibold leading-snug text-[color:var(--ink)]">
          {current.question}
        </h3>
        {current.hint && <p className="mt-2 text-[0.8125rem] text-[color:var(--ink-2)]">{current.hint}</p>}
        <div className={`mt-4 grid gap-2 pb-2 ${current.columns === 2 ? "grid-cols-2" : ""}`}>
          {current.options.map((option) => {
            const priority = current.multi ? survey.priorities.indexOf(option.value) : -1;
            return (
              <OptionButton
                key={option.value}
                label={option[lang] || option.de}
                selected={current.multi ? priority >= 0 : survey[current.field] === option.value}
                badge={priority >= 0 ? priority + 1 : null}
                onClick={() => onPick(current, option.value)}
              />
            );
          })}
        </div>
      </div>

      <div className="shrink-0 pt-4">
        {surveyStatus === "error" && (
          <p className="pb-3 text-[0.9375rem] text-[#a4593f]">{surveyText.error}</p>
        )}
        {isLast ? (
          <button
            type="button"
            disabled={!answered || surveyStatus === "saving"}
            onClick={onSave}
            className="btn btn-primary w-full justify-center"
          >
            {surveyStatus === "saving" ? surveyText.saving : surveyText.save}
          </button>
        ) : (
          <button
            type="button"
            disabled={!answered}
            onClick={() => go(step + 1, "next")}
            className="btn btn-primary w-full justify-center"
          >
            {surveyText.next}
            <FiArrowRight aria-hidden className="h-[14.4px] w-[14.4px]" />
          </button>
        )}
      </div>
    </div>
  );
}

function WaitlistModal({ open, mode, onClose, initialEmail }) {
  const { WAITLIST } = useSteffiCopy();
  const { lang } = usePublicLang();
  const isMobile = useIsMobile();
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [cityStatus, setCityStatus] = useState("idle");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  // "Eintragen" stays grey until the form is complete but still takes the
  // click; a click while grey sets this so the dialog can say what is missing.
  const [attempted, setAttempted] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [survey, setSurvey] = useState({ audience: "", age_group: "", priorities: [] });
  const [surveyStatus, setSurveyStatus] = useState("idle");
  const surveyText = SURVEY_TEXT[lang] || SURVEY_TEXT.de;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);

    // `overflow: hidden` alone does not hold on iOS Safari: the page keeps
    // scrolling behind the dialog and drags the fixed overlay with it, which
    // crops the top of the card. Pinning the body is what actually stops it —
    // at the cost of having to restore the scroll position by hand.
    const scrollY = window.scrollY;
    const previous = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.removeEventListener("keydown", onKey);
      Object.assign(document.body.style, previous);
      window.scrollTo(0, scrollY);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && initialEmail) setEmail(initialEmail);
  }, [open, initialEmail]);

  useEffect(() => {
    if (open) return;
    setEmail("");
    setCity("");
    setCityOptions([]);
    setCityStatus("idle");
    setPhone("");
    setConsent(false);
    setAttempted(false);
    setStatus("idle");
    setMessage("");
    setSurvey({ audience: "", age_group: "", priorities: [] });
    setSurveyStatus("idle");
  }, [open]);

  useEffect(() => {
    const query = city.trim();
    if (!open || query.length < 2) {
      setCityOptions([]);
      setCityStatus("idle");
      return undefined;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setCityStatus("loading");
      const params = new URLSearchParams({ q: query, lang });
      try {
        const response = await fetch(`${API}/city-search?${params}`, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error();
        const data = await response.json();
        setCityOptions(Array.isArray(data.items) ? data.items : []);
        setCityStatus("done");
      } catch (error) {
        if (error.name !== "AbortError") {
          setCityOptions([]);
          setCityStatus("error");
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [city, lang, open]);

  const cityIsValid = cityOptions.some((option) => option.name === city.trim());
  const cityIsRequired = mode === "waitlist";
  const cityIsInvalid = (cityIsRequired || Boolean(city.trim())) && !cityIsValid;
  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = emailIsValid && consent && !cityIsInvalid;
  const showEmailError = attempted && !emailIsValid;
  const showConsentError = attempted && !consent;

  if (!open) return null;

  async function onSubmit(event) {
    event.preventDefault();
    if (!canSubmit) {
      setAttempted(true);
      return;
    }
    setStatus("sending");
    setMessage("");
    try {
      const res = await fetch(`${API}/newsletter-subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          intent: mode,
          email,
          city,
          ...(mode === "waitlist" && { phone }),
          source: mode === "waitlist" ? "steffi-waitlist" : "steffi-launch",
          language: lang,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || WAITLIST.error);
      setStatus("done");
      setMessage(mode === "waitlist" ? surveyText.waitlistSaved : data.confirmed ? surveyText.confirmed : surveyText.confirmation);
      void trackAnalyticsEvent(`${mode}_signup`, { form_location: `steffi_${mode}` });
    } catch (err) {
      setStatus("error");
      setMessage(err.message || WAITLIST.error);
    }
  }

  async function saveSurvey() {
    setSurveyStatus("saving");
    try {
      const res = await fetch(`${API}/newsletter-subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, survey }),
      });
      if (!res.ok) throw new Error();
      setSurveyStatus("done");
      void trackAnalyticsEvent("newsletter_survey_completed", { form_location: `steffi_${mode}` });
    } catch {
      setSurveyStatus("error");
    }
  }

  function togglePriority(value) {
    setSurvey((current) => ({
      ...current,
      priorities: current.priorities.includes(value)
        ? current.priorities.filter((entry) => entry !== value)
        : current.priorities.length < 3
          ? [...current.priorities, value]
          : current.priorities,
    }));
  }

  const ageQuestion = survey.audience === "self"
    ? surveyText.ageSelf
    : survey.audience === "professional"
      ? surveyText.ageProfessional
      : surveyText.ageOther;

  const steps = [
    { field: "audience", question: surveyText.audience, options: NEWSLETTER_AUDIENCES },
    { field: "age_group", question: ageQuestion, options: NEWSLETTER_AGE_GROUPS, columns: 2 },
    {
      field: "priorities",
      question: surveyText.benefit,
      hint: surveyText.benefitHint,
      options: NEWSLETTER_BENEFITS,
      multi: true,
    },
  ];

  // The wizard replaces the marketing header too: on a phone the headline and
  // body alone fill the screen, so every step would start below the fold.
  const wizard = isMobile && status === "done" && surveyStatus !== "done";

  return (
    <div
      className="steffi waitlist-overlay fixed inset-x-0 top-0 z-[120] flex items-center justify-center px-[36px] py-10 sm:px-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby={wizard ? "steffi-wizard-title" : "steffi-waitlist-title"}
    >
      <button
        type="button"
        aria-label={WAITLIST.close}
        onClick={onClose}
        className="absolute inset-0 bg-[#24211e]/25 backdrop-blur-[5px]"
      />
      <div
        className={`card waitlist-card relative w-full max-w-[30rem] ${
          wizard ? "flex flex-col overflow-hidden p-6" : "overflow-y-auto p-8 sm:p-10"
        }`}
        style={{ borderRadius: 32, boxShadow: "0 40px 90px -40px rgba(36,33,30,.45)" }}
      >
        <button
          type="button"
          aria-label={WAITLIST.close}
          onClick={onClose}
          className="absolute right-5 top-5 z-10 grid h-10 w-10 place-items-center rounded-full text-[color:var(--ink-3)] transition hover:bg-[color:var(--bg-2)] hover:text-[color:var(--ink)]"
        >
          <FiX className="h-5 w-5" aria-hidden />
        </button>

        {wizard ? (
          <SurveyWizard
            steps={steps}
            survey={survey}
            lang={lang}
            surveyText={surveyText}
            surveyStatus={surveyStatus}
            onPick={(step, value) =>
              step.multi
                ? togglePriority(value)
                : setSurvey((current) => ({ ...current, [step.field]: value }))
            }
            onSave={saveSurvey}
          />
        ) : (
          <>
            <p className="t-eyebrow">{WAITLIST.eyebrow}</p>
            <h2 id="steffi-waitlist-title" className="mt-4 text-[2rem] leading-[1.08]">
              {WAITLIST.title[0]}
              <br />
              {WAITLIST.title[1]}
            </h2>
            <p className="mt-4 text-[1.0625rem] leading-relaxed text-[color:var(--ink-2)]">
              {mode === "waitlist" ? surveyText.waitlistBody : WAITLIST.body}
            </p>

            {status === "done" ? (
              <div className="mt-7">
                <p className="flex items-start gap-3 rounded-3xl bg-[color:var(--bg-2)] px-5 py-4 text-[1.0625rem] text-[color:var(--ink)]">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[color:var(--sage)] text-white">
                    <FiCheck className="h-3.5 w-3.5" aria-hidden />
                  </span>
                  {message}
                </p>
                {surveyStatus === "done" ? (
                  <p className="mt-7 text-center text-[1.125rem] font-semibold text-[color:var(--ink)]">{surveyText.thanks}</p>
                ) : (
                  <div className="mt-7">
                    <p className="text-[0.9375rem] leading-relaxed text-[color:var(--ink-2)]">{surveyText.intro}</p>
                    {steps.slice(0, 2).map((step, index) => (
                      <label key={step.field} className="mt-5 block text-[0.9375rem] font-semibold text-[color:var(--ink)]">
                        {index + 1}. {step.question}
                        <select
                          required
                          value={survey[step.field]}
                          onChange={(event) => setSurvey((current) => ({ ...current, [step.field]: event.target.value }))}
                          className="mt-2 w-full rounded-2xl border border-[color:var(--border)] bg-[color:var(--bg)] px-4 py-3 font-normal text-[color:var(--ink)] outline-none focus:border-[color:var(--accent)]"
                        >
                          <option value="">—</option>
                          {step.options.map((option) => <option key={option.value} value={option.value}>{option[lang] || option.de}</option>)}
                        </select>
                      </label>
                    ))}
                    <fieldset className="mt-5">
                      <legend className="text-[0.9375rem] font-semibold text-[color:var(--ink)]">3. {surveyText.benefit}</legend>
                      <p className="mt-1 text-[0.8125rem] text-[color:var(--ink-2)]">{surveyText.benefitHint}</p>
                      <div className="mt-3 grid gap-2">
                        {NEWSLETTER_BENEFITS.map((option) => {
                          const priority = survey.priorities.indexOf(option.value);
                          return (
                            <OptionButton
                              key={option.value}
                              label={option[lang] || option.de}
                              selected={priority >= 0}
                              badge={priority >= 0 ? priority + 1 : null}
                              onClick={() => togglePriority(option.value)}
                            />
                          );
                        })}
                      </div>
                    </fieldset>
                    {surveyStatus === "error" && <p className="mt-3 text-[0.9375rem] text-[#a4593f]">{surveyText.error}</p>}
                    <button type="button" disabled={!survey.audience || !survey.age_group || !survey.priorities.length || surveyStatus === "saving"} onClick={saveSurvey} className="btn btn-primary mt-5 w-full justify-center disabled:opacity-50">{surveyStatus === "saving" ? surveyText.saving : surveyText.save}</button>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate autoComplete="off" className="mt-7 flex flex-col gap-3">
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={WAITLIST.placeholder}
                  aria-invalid={showEmailError}
                  className={`w-full rounded-full border bg-[color:var(--bg)] px-6 py-4 text-[1.0625rem] text-[color:var(--ink)] outline-none transition placeholder:text-[color:var(--ink-3)] ${
                    showEmailError
                      ? "border-[#a4593f] focus:border-[#a4593f]"
                      : "border-[color:var(--border)] focus:border-[color:var(--accent)]"
                  }`}
                />
                {showEmailError && (
                  <p role="alert" className="-mt-1 flex items-start gap-2 px-4 text-[0.9375rem] leading-snug text-[#a4593f]">
                    <FiAlertCircle aria-hidden className="mt-0.5 shrink-0" /> {WAITLIST.emailRequired}
                  </p>
                )}
                <div>
                  <input
                    type="search"
                    name="steffi-waitlist-query"
                    required={cityIsRequired}
                    autoComplete="off"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls="steffi-city-options"
                    aria-expanded={cityStatus === "done" && cityOptions.length > 0 && !cityIsValid}
                    value={city}
                    onChange={(event) => setCity(event.target.value)}
                    placeholder={cityIsRequired ? surveyText.city : surveyText.cityOptional}
                    className="w-full rounded-full border border-[color:var(--border)] bg-[color:var(--bg)] px-6 py-4 text-[1.0625rem] text-[color:var(--ink)] outline-none transition placeholder:text-[color:var(--ink-3)] focus:border-[color:var(--accent)]"
                  />
                  {cityStatus === "done" && cityOptions.length > 0 && !cityIsValid && (
                    <div id="steffi-city-options" role="listbox" className="mt-1 max-h-52 w-full overflow-y-auto rounded-2xl border border-[color:var(--border)] bg-[color:var(--bg)] shadow-lg">
                      {cityOptions.map((option) => (
                        <button
                          key={option.name}
                          type="button"
                          role="option"
                          aria-selected="false"
                          onClick={() => setCity(option.name)}
                          className="flex w-full items-center justify-between gap-4 border-b border-[color:var(--border)] px-5 py-3 text-left text-[0.9375rem] text-[color:var(--ink)] transition last:border-b-0 hover:bg-[color:var(--bg-2)]"
                        >
                          <span>{option.label}</span>
                          {option.state && <span className="shrink-0 text-xs text-[color:var(--ink-3)]">{option.state}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {cityStatus === "loading" && <p className="mt-1 px-2 text-xs text-[color:var(--ink-3)]">{surveyText.citySearching}</p>}
                  {cityStatus === "error" && <p className="mt-1 px-2 text-xs text-[#a4593f]">{surveyText.cityError}</p>}
                  {(cityStatus === "done" || attempted) && cityIsInvalid && <p className="mt-1 px-2 text-xs text-[#a4593f]">{cityOptions.length || !city.trim() ? surveyText.citySelect : surveyText.cityNoResults}</p>}
                  <a
                    href="https://www.openstreetmap.org/copyright"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block px-2 text-[0.625rem] text-[color:var(--ink-3)] hover:underline"
                  >
                    {surveyText.cityAttribution}
                  </a>
                </div>
                {mode === "waitlist" && (
                  <input
                    type="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder={surveyText.phone}
                    className="w-full rounded-full border border-[color:var(--border)] bg-[color:var(--bg)] px-6 py-4 text-[1.0625rem] text-[color:var(--ink)] outline-none transition placeholder:text-[color:var(--ink-3)] focus:border-[color:var(--accent)]"
                  />
                )}
                <label
                  className={`flex items-start gap-3 rounded-2xl border px-3 py-2 text-[0.9375rem] leading-relaxed text-[color:var(--ink-2)] transition ${
                    showConsentError ? "border-[#a4593f] bg-[#a4593f]/[0.06]" : "border-transparent"
                  }`}
                >
                  <input
                    type="checkbox"
                    required
                    aria-invalid={showConsentError}
                    aria-describedby={showConsentError ? "waitlist-consent-error" : undefined}
                    checked={consent}
                    onChange={(event) => setConsent(event.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-[color:var(--accent)]"
                  />
                  <span>
                    {WAITLIST.consentLead}{" "}
                    <Link
                      to="/datenschutzhinweis"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-4 hover:text-[color:var(--ink)]"
                    >
                      {WAITLIST.consentLink}
                    </Link>
                    {WAITLIST.consentTrail}
                  </span>
                </label>
                {showConsentError && (
                  <p
                    id="waitlist-consent-error"
                    role="alert"
                    className="-mt-1 flex items-start gap-2 px-4 text-[0.9375rem] leading-snug text-[#a4593f]"
                  >
                    <FiAlertCircle aria-hidden className="mt-0.5 shrink-0" /> {WAITLIST.consentRequired}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  aria-disabled={!canSubmit}
                  className="btn btn-primary mt-2 justify-center"
                >
                  {status === "sending" ? WAITLIST.sending : WAITLIST.submit}
                  <FiArrowRight aria-hidden className="h-[14.4px] w-[14.4px]" />
                </button>
                {status === "error" && message && (
                  <p className="px-2 text-[0.9375rem] text-[#a4593f]">{message}</p>
                )}
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
